import { ecorEnv } from '@/../environments/environment';
import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventEnum, ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { SurveyCard } from '@/surveys/states/surveys/survey-card';
import { Component, HostListener, OnInit, signal, computed, inject, DestroyRef } from '@angular/core';
import { Validators, ReactiveFormsModule, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ShareSurveyAccessDialogData } from '../../dialogs-data/share-survey-access-dialog-data';
import { ShareSurveyAccessDialogHttpService } from '../../services/share-survey-access-dialog-http.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { NgxMaskModule } from 'ngx-mask';
import { LinearLoadingComponent } from '@/common/components/linear-loading/linear-loading.component';

@Component({
    selector: 'app-share-survey-access-dialog',
    templateUrl: './share-survey-access-dialog.component.html',
    styleUrls: ['./share-survey-access-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTabsModule,
        NgxMaskModule,
        LinearLoadingComponent
    ],
    providers: [
        ParadataEventService,
        ShareSurveyAccessDialogHttpService
    ]
})
export class ShareSurveyAccessDialogComponent implements OnInit {
    private destroyRef = inject(DestroyRef);
    private paradataEventService = inject(ParadataEventService);
    private store = inject(Store<PortalUserState>);
    private shareSurveyAccessDialogHttpService = inject(ShareSurveyAccessDialogHttpService);
    private matDialogRef = inject(MatDialogRef<ShareSurveyAccessDialogComponent>);

    public shareSurveyAccessDialogData = inject<ShareSurveyAccessDialogData>(MAT_DIALOG_DATA);

    private user = signal<PortalUser | null>(null);
    private success = signal<boolean>(false);
    private updateMessage = signal<string>('');
    private sendingRequest = signal<boolean>(false);
    private delegationError = signal<string>('');
    activeRequests = signal<any[]>([]);
    inactiveRequests = signal<any[]>([]);
    private delegates = signal<any[]>([]);
    private survey = signal<SurveyCard | null>(null);
    private emails = signal<string[]>([]);
    private noDelegatees = signal<boolean>(true);
    private delegationSuccess = signal<boolean>(false);
    private confirmRequest = signal<boolean>(false);
    private tableCreated = signal<boolean>(false);
    private reverse = signal<boolean>(true);
    private newEmails = signal<any[]>([]);
    private order = signal<string>('');
    private allowRemoval = signal<boolean>(false);
    loadingDelegates = signal<boolean>(true);

    currentUser = computed(() => this.user());
    isSuccess = computed(() => this.success());
    currentUpdateMessage = computed(() => this.updateMessage());
    isSendingRequest = computed(() => this.sendingRequest());
    currentDelegationError = computed(() => this.delegationError());
    currentActiveRequests = computed(() => this.activeRequests());
    currentInactiveRequests = computed(() => this.inactiveRequests());
    currentDelegates = computed(() => this.delegates());
    currentSurvey = computed(() => this.survey());
    currentEmails = computed(() => this.emails());
    isNoDelegatees = computed(() => this.noDelegatees());
    isDelegationSuccess = computed(() => this.delegationSuccess());
    isConfirmRequest = computed(() => this.confirmRequest());
    isTableCreated = computed(() => this.tableCreated());
    isReverse = computed(() => this.reverse());
    currentNewEmails = computed(() => this.newEmails());
    currentOrder = computed(() => this.order());
    isAllowRemoval = computed(() => this.allowRemoval());

    cpassEnabled = ecorEnv.cpassEnabled;
    placeholder = 'Enter Email Address';

    formGroup = new UntypedFormGroup({
        note: new UntypedFormControl('', [])
    });

    constructor() {
        this.matDialogRef.disableClose = true;
        this.store.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(user => {
                this.user.set(user);
            });
    }

    ngOnInit() {
        this.sendingRequest.set(true);
        this.addInput();

        if (!this.shareSurveyAccessDialogData) {
            console.error('ShareSurveyAccessDialogData is undefined');
            this.sendingRequest.set(false);
            this.loadingDelegates.set(false);
            return;
        }

        if (!this.shareSurveyAccessDialogData.survey) {
            console.error('Survey data is undefined in ShareSurveyAccessDialogData');
            this.sendingRequest.set(false);
            this.loadingDelegates.set(false);
            return;
        }

        this.survey.set(this.shareSurveyAccessDialogData.survey);
        const currentSurvey = this.currentSurvey();
        if (currentSurvey) {
            this.paradataEventService.add(ParadataEventEnum.ShareSurveyAccess, currentSurvey.surveyId);
            this.shareSurveyAccessDialogHttpService.getSurveyDelegates(currentSurvey.surveyInstanceId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response) => {
                        this.createDelegationTable(response);
                        this.sendingRequest.set(false);
                        this.loadingDelegates.set(false);
                    },
                    error: (error: any) => {
                        this.delegationError.set('Delegation Requests could not be displayed');
                        this.updateMessage.set('');
                        this.sendingRequest.set(false);
                        this.loadingDelegates.set(false);
                    }
                });
        }
    }

    addInput() {
        const id = 'email' + Date.now();
        const currentDelegates = this.currentDelegates();
        this.delegates.set([...currentDelegates, { id: id, email: '' }]);
        this.formGroup.addControl(id, new UntypedFormControl('', [Validators.email, Validators.required]));
    }

    removeInput(id: string) {
        const currentDelegates = this.currentDelegates();
        this.delegates.set(currentDelegates.filter(item => item.id !== id));
        this.formGroup.removeControl(id);
        this.delegationError.set('');
    }

    addMore() {
        this.confirmRequest.set(false);
        this.delegationSuccess.set(false);
        this.updateMessage.set('');
        this.delegates.set([]);
        this.formGroup = new UntypedFormGroup({
            note: new UntypedFormControl('', [])
        });
        this.addInput();
    }

    edit() {
        this.confirmRequest.set(false);
        this.delegationSuccess.set(false);
    }

    showError(field: string) {
        const formField = this.formGroup.get(field);
        return formField.invalid && !formField.pristine;
    }

    createDelegationTable(response: any[]) {
        response.forEach(delegate => delegate.createdDate = delegate.createdDate ? new Date(delegate.createdDate) : null);
        this.activeRequests.set(response.filter(delegate => delegate.status !== 'Removed'));
        this.inactiveRequests.set(response.filter(delegate => delegate.status === 'Removed'));
        this.noDelegatees.set(this.currentActiveRequests().length <= 0);
        this.allowRemoval.set(response.filter(delegate => delegate.status === 'Accepted').length > 1);
        this.tableCreated.set(true);
    }

    dupeChecker(array: string[]) {
        return new Set(array).size !== array.length;
    }

    delegateSurvey() {
        const currentDelegates = this.currentDelegates();
        const emails = currentDelegates.map(item => item.email);
        this.emails.set(emails);
        const dups = this.dupeChecker(emails);
        if (dups === true) {
            this.delegationError.set('Duplicate email found. Please correct and try again.');
            return;
        }
        const note = this.formGroup.get('note').value;
        this.confirmRequest.set(false);
        this.delegationSuccess.set(false);
        this.sendingRequest.set(true);
        const currentSurvey = this.currentSurvey();
        const currentUser = this.currentUser();
        
        if (currentSurvey && currentUser) {
            this.paradataEventService.add(ParadataEventEnum.ConfirmShareSurveyAccess, currentSurvey.surveyId);

            this.shareSurveyAccessDialogHttpService.addSurveyDelegates(currentSurvey.surveyInstanceId, emails.toString(), note, currentUser.userAccountId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response) => {
                        const newEmails: any[] = [];
                        const currentActiveRequests = this.currentActiveRequests();

                        for (let k = 0; k < response.length; k++) {
                            newEmails.push(response[k].delegateeEmailAddress);
                            response[k].enableResend = 'Y';
                            currentActiveRequests.unshift(response[k]);
                        }

                        this.newEmails.set(newEmails);
                        this.activeRequests.set(currentActiveRequests);
                        this.delegationSuccess.set(true);
                        this.sendingRequest.set(false);

                        for (let i = 0; i < currentActiveRequests.length; i++) {
                            if ((currentActiveRequests[i].delegateeEmailAddress === newEmails.map(n => n.delegateeEmailAddress)) && (currentActiveRequests[i].status === 'Revoked')) {
                                currentActiveRequests[i].enableResend = 'N';
                            }
                        }
                    },
                    error: (error: any) => {
                        console.error('share-survey-access-dialog', 'addSurveyDelegates', 'error', error);

                        this.updateMessage.set('');
                        this.delegationSuccess.set(false);
                        this.sendingRequest.set(false);
                        const errors = {
                            'EC-120': 'User account not found',
                            'EC-129': 'Authentication Code could not be validated',
                            'EC-130': 'Authentication Code is disabled',
                            'EC-131': 'Authentication Code is expired',
                            'EC-127': 'At least one of the user(s) had already been sent a request to share this survey.',
                            'EC-147': 'At least one of the user(s) had already accepted a request to share this survey.'
                        };

                        this.delegationError.set(errors[error.error.message] || 'Share survey access request failed.');
                    }
                });
        }
    }

    isFormValid() {
        this.confirmRequest.set(true);
        this.delegationError.set('');
        this.updateMessage.set('');
        this.sendingRequest.set(false);
    }

    handleDelegation(action: string, requestMapping: string, activeRequest: any) {
        const currentUser = this.currentUser();
        const currentSurvey = this.currentSurvey();

        if (!currentUser || !currentSurvey) {
            return;
        }

        this.sendingRequest.set(true);
        this.updateMessage.set('');
        this.delegationError.set('');
        this.shareSurveyAccessDialogHttpService.handleDelegation(action, requestMapping, activeRequest.surveyDelegationRequestId, currentUser.userAccountId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    if (action === 'resend') {
                        this.paradataEventService.add(ParadataEventEnum.ResentRequest, currentSurvey.surveyId);
                        this.updateMessage.set('Your request to share survey access has been resent to ' + activeRequest.delegateeEmailAddress + '.');
                    }
                    if (action === 'revoke') {
                        this.updateMessage.set('Your request to revoke survey access was successful.');
                        this.paradataEventService.add(ParadataEventEnum.RemoveDelegates, currentSurvey.surveyId);
                    }
                    this.delegationError.set('');
                    if (action === 'resend' && response !== null) {
                        response.enableResend = 'Y';
                        const currentRequests = this.activeRequests();
                        currentRequests.unshift(response);
                        this.activeRequests.set([...currentRequests]);
                        activeRequest.enableResend = 'N';
                    }

                    if (action !== 'resend') {
                        this.allowRemoval.set(false);
                        for (let i = 0; i < this.activeRequests().length; i++) {
                            if (this.activeRequests()[i].surveyDelegationRequestId === activeRequest.surveyDelegationRequestId) {
                                if (this.activeRequests()[i].status === 'Accepted') {
                                    const currentActive = this.activeRequests();
                                    const currentInactive = this.inactiveRequests();
                                    currentActive[i].status = 'Removed';
                                    currentInactive.push(currentActive[i]);
                                    currentActive.splice(i, 1);
                                    this.activeRequests.set([...currentActive]);
                                    this.inactiveRequests.set([...currentInactive]);
                                } else {
                                    const currentActive = this.activeRequests();
                                    currentActive[i].status = 'Revoked';
                                    this.activeRequests.set([...currentActive]);
                                }
                            }
                        }

                        this.allowRemoval.set(this.activeRequests().filter(request => request.status === 'Accepted').length > 1);
                    }
                    this.sendingRequest.set(false);
                },
                error: (error: any) => {
                    const errors = {
                        'EC-129': 'Authentication Code could not be validated',
                        'EC-130': 'Authentication Code is disabled',
                        'EC-131': 'Authentication Code is expired',
                    };

                    this.delegationError.set(errors[error.error.message] || 'An error ocurred while attempting to ' + action + ' access to this survey.');
                    this.sendingRequest.set(false);
                }
            });
    }

    fasClass(column: string) {
        if (this.currentOrder() === column) {
            if (this.isReverse()) {
                return 'fa-sort-down';
            } else {
                return 'fa-sort-up';
            }
        } else {
            return 'fa-sort';
        }
    }

    ariaSort(column: string) {
        if (this.currentOrder() === column) {
            if (this.isReverse()) {
                return 'descending';
            } else {
                return 'ascending';
            }
        } else {
            return 'none';
        }
    }

    reorder(attrib: string, requests: any) {
        if (this.currentOrder() !== attrib) {
            this.reverse.set(false);
            this.order.set(attrib);
        } else {
            this.reverse.set(!this.isReverse());
        }

        requests().sort((a, z) => {
            if (typeof a[attrib] === 'undefined' || a[attrib] == null) {
                if (this.isReverse()) {
                    return 1;
                } else {
                    return -1;
                }
            }

            if (typeof z[attrib] === 'undefined' || z[attrib] == null) {
                if (this.isReverse()) {
                    return -1;
                } else {
                    return 1;
                }
            }

            if (this.isReverse()) {
                if (attrib === 'createdDate') {
                    return z[attrib].getTime() - a[attrib].getTime();
                } else {
                    return z[attrib].localeCompare(a[attrib], 'en', { sensitivity: 'base' });
                }
            } else {
                if (attrib === 'createdDate') {
                    return a[attrib].getTime() - z[attrib].getTime();
                } else {
                    return a[attrib].localeCompare(z[attrib], 'en', { sensitivity: 'base' });
                }
            }
        });

        requests.set([...requests()]);
    }

    selectedTabChanged($event: MatTabChangeEvent) {
        const currentSurvey = this.currentSurvey();

        if (!currentSurvey) {
            return;
        }

        if ($event.index === 0) {
            this.paradataEventService.add(ParadataEventEnum.CurrentDelegates, currentSurvey.surveyId);
        } else {
            this.paradataEventService.add(ParadataEventEnum.RemoveDelegates, currentSurvey.surveyId);
        }
    }

    cancel() {
        this.paradataEventService.add(ParadataEventEnum.CancelShareSurveyAccessRequest1);
        this.close('cancel');
    }

    close(value: any) {
        this.matDialogRef.close(value);
    }

    @HostListener('keydown.esc')
    public onEsc() {
        this.close('ESC keydown');
    }
}